import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { ThemeManagerProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import {
  DevAuthHelperRoute,
  IframeSyncHandler,
  SyncSession,
} from './components/auth';
import { SignInGateProvider } from './components/auth/useRequireSignIn';
import { CreditsProvider } from './credits/useCredits';
import { SessionDataGuard } from './components/auth/SessionDataGuard';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AssetGalleryRoute, LabRoute } from './routes';
import { DashboardRoute } from './routes/dashboard/DashboardRoute';
import { ProviderKeysPage } from './routes/settings/ProviderKeysPage';
import { BillingPage } from './routes/settings/BillingPage';
import { AccountPage } from './routes/settings/AccountPage';
import { createQueryClient } from './queries/queryClient';
import { AnalyticsPageTracker } from './analytics/AnalyticsPageTracker';

function App() {
  // Per spec §4.2 the provider order is Theme → Query → Auth → Router →
  // ErrorBoundary → Routes. A single QueryClient instance for the app life
  // (StrictMode double-mount is fine; the client itself handles re-entrancy).
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <ThemeManagerProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <IframeSyncHandler />
          <SessionDataGuard />
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <AnalyticsPageTracker />
            <ErrorBoundary>
              <SignInGateProvider>
              <CreditsProvider>
              <Routes>
                <Route path="/dev-auth-helper" element={<DevAuthHelperRoute />} />
                <Route path="/sync-session" element={<SyncSession />} />

                {/* Public by design. Everything the backend serves anonymously —
                    labs, studios, swatches, operation catalogs, model pickers — is
                    browsable without an account, so a visitor can see what the
                    product does before deciding to sign up. Authentication is
                    enforced at the point of CREATING or SAVING instead, by
                    SignInGateProvider (see useRequireSignIn), which is both where
                    it costs money and where work needs an owner to scope it to.
                    User-scoped surfaces (gallery, API keys) render a signed-out
                    state rather than redirecting. */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardRoute />} />
                <Route path="/labs/:labId" element={<LabRoute />} />
                <Route path="/labs/:labId/:studioId" element={<LabRoute />} />
                <Route path="/assets" element={<AssetGalleryRoute />} />
                <Route path="/collections/:collectionId" element={<AssetGalleryRoute />} />
                <Route path="/settings/keys" element={<ProviderKeysPage />} />
                <Route path="/settings/billing" element={<BillingPage />} />
                <Route path="/settings/account" element={<AccountPage />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </CreditsProvider>
              </SignInGateProvider>
            </ErrorBoundary>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeManagerProvider>
  );
}

export default App;
